import getFiltersFromGrid from '@/features/filters/helpers/getFiltersFromGrid';
import { IChoiceFilters } from '@/interfaces/choice.interfaces';
import { FilterBlockType, IFilterGrid } from '@/interfaces/constructor.interfaces';
import {
  IExtendedFilterCollectionItem,
  IFilterPrimitive,
  IFiltersData,
} from '@/interfaces/filters.interfaces';
import instanceOfFilterPrimitive from '@/utils/instanceOfFilterPrimitive';
import instanceOfReference from '@/utils/instanceOfReference';

/**
 * Вспомогательная функция, возвращающая значение фильтра в подходящем формате
 * для установки в стор.
 * @param filterValue полное значение фильтра
 * @param type тип фильтра
 */
function getFilterValue(
  filterValue: IExtendedFilterCollectionItem | IFilterPrimitive,
  type: FilterBlockType,
): unknown {
  switch (type) {
    case FilterBlockType.FilterSelect:
      if (instanceOfReference(filterValue)) {
        return filterValue.id;
      }
      if (instanceOfFilterPrimitive(filterValue)) {
        return filterValue.value;
      }
      return;
    case FilterBlockType.FilterMultipleSelect:
    case FilterBlockType.FilterLocation:
    case FilterBlockType.FilterColorPicker:
    case FilterBlockType.FilterCheckboxGroup:
    case FilterBlockType.FilterModelCheckboxGroup:
      if (instanceOfReference(filterValue)) {
        return [filterValue.id];
      }
      if (instanceOfFilterPrimitive(filterValue)) {
        return [filterValue.value];
      }
      return;
    default:
      throw new Error(
        `Виджет АВН. Тип фильтра ${type} не поддерживается для автоматической установки значения.`,
      );
  }
}

/**
 * Возвращает массив ключей фильтров и их значений настройка,
 * для фильтров, у которых установлена опция autoSelectSingleOption
 * При этом сам фильтр должен иметь одно единственное возможное значение для установки
 * @param grid грид фильтров
 * @param data данные для фильтров.
 */
export const getFiltersForAutoSetup = (
  grid: IFilterGrid,
  data: IFiltersData,
  choice: IChoiceFilters,
): Array<IChoiceFilters> => {
  const filters = getFiltersFromGrid(grid);

  // Отбираем только фильтры, у которых установлена
  // опция автоматического выбора единственного значения
  const filtersEnabledForAutoSelect = filters.filter(filter =>
    Boolean(filter.autoSelectSingleOption),
  );

  // Выкидываем фильтры, значения которых уже проставлены
  const filtersToSelect = filtersEnabledForAutoSelect.filter(
    filter => !(filter.key in choice),
  );

  const singleValuesFilterData: {
    [key: string]: IExtendedFilterCollectionItem | IFilterPrimitive;
  } = {};

  // Собираем объект с данными, для вариантов значений
  // фильтра, в которых доступна только один вариант.
  Object.keys(data).forEach(key => {
    const filterValues = data[key];

    if (!Array.isArray(filterValues)) {
      return;
    }

    const availableFilterValues = [...filterValues].filter(
      value => value && value.available,
    );

    if (availableFilterValues.length === 1) {
      const [singleFilterValue] = availableFilterValues;
      singleValuesFilterData[key] = singleFilterValue;
    }
  });

  // Подготавливаем объект фильтров и значений для авто-установки
  const autoSelectFilterValues = filtersToSelect.reduce((acc, { key, type }) => {
    const filterValue = singleValuesFilterData[key];

    if (filterValue) {
      const value = getFilterValue(filterValue, type);
      acc.push({
        [key]: value,
      });
    }

    return acc;
  }, [] as Array<IChoiceFilters>);

  return autoSelectFilterValues;
};

export default getFiltersForAutoSetup;
